<template>
  <ValidationObserver ref="validationObserver" v-slot="{ errors }" class="h-full">
    <div class="px-3 pt-2 h-full overflow-auto pb-5">
      <FormSelect
        v-model="payload.flow_id"
        :options="flows"
        :label="$t('projects.flows.flow')"
        class="mt-2"
        name="flowid"
        @update:modelValue="emitPayload"
        :error="errors.flowid"
        rules="required"
        append-to-body
        default-reducer
      />

      <template v-if="!isAgent">
        <div class="flex flex-row">
          <FormSelect
            v-model="payload.schedule_type"
            label="Assignment Type"
            :reduce="(option) => option.value"
            class="w-30 mt-2 mr-2 flex-shrink-0"
            :searchable="false"
            :clearable="false"
            appendToBody
            :options="[
              { label: $t('variable'), value: 'variable' },
              { label: $t('value'), value: 'value' },
            ]"
            @option:selected="emitPayload"
          ></FormSelect>

          <VariableSelect
            v-if="payload.schedule_type === 'variable'"
            v-model="payload.variable"
            label="Schedule Variable"
            :filter-data-type="['Schedule']"
            class="w-50 mt-2"
            :placeholder="$t('evaluate.select_variable')"
            @update:modelValue="emitPayload"
          ></VariableSelect>

          <!-- <FormSelect
          v-if="payload.schedule_type === 'value'"
          v-model="payload.recurring"
          :options="[
            { label: 'Every', value: true },
            { label: 'Once', value: false },
          ]"
          :label="$t('evaluate.recurring')"
          class="mt-2 w-40"
          append-to-body
          default-reducer
          @update:modelValue="emitPayload"
        /> -->

          <FormSelect
            v-if="payload.schedule_type === 'value'"
            v-model="payload.frequency"
            :options="scheduleOptions"
            :label="$t('evaluate.schedule_flow')"
            class="mt-2 ml-2 w-40"
            append-to-body
            default-reducer
            @update:modelValue="emitPayload"
          />

          <FormSelect
            :label="$t('projects.endpoints.minute')"
            v-if="payload.schedule_type === 'value' && payload.frequency === 'minute'"
            v-model="payload.schedule"
            :options="minuteList"
            class="mt-2 ml-2 w-30"
            :reduce="(option) => option.value"
            @update:modelValue="emitPayload"
          />
          <FormSelect
            :label="$t('projects.endpoints.hour')"
            v-if="payload.schedule_type === 'value' && payload.frequency === 'hour'"
            v-model="payload.schedule"
            :options="hoursList"
            class="mt-2 ml-2 w-30"
            :reduce="(option) => option.value"
            @update:modelValue="emitPayload"
          />
          <FormSelect
            :label="$t('projects.endpoints.day')"
            v-if="payload.schedule_type === 'value' && payload.frequency === 'day'"
            v-model="payload.schedule"
            :options="daysList"
            class="mt-2 ml-2 w-30"
            :reduce="(option) => option.value"
            @update:modelValue="emitPayload"
          />
          <FormSelect
            :label="$t('projects.endpoints.weekday')"
            v-if="payload.schedule_type === 'value' && payload.frequency === 'weekday'"
            v-model="payload.schedule"
            :options="weekDayList"
            append-to-body
            class="mt-2 ml-2 w-30"
            :reduce="(option) => option.value"
            @update:modelValue="emitPayload"
          />
          <FormSelect
            :label="$t('projects.endpoints.day')"
            v-if="payload.schedule_type === 'value' && (payload.frequency === 'month' || payload.frequency === 'bi-month' || payload.frequency === 'quarter')"
            v-model="payload.schedule"
            :options="dayList"
            append-to-body
            :reduce="(option) => option.value"
            class="mt-2 ml-2 w-30"
            @update:modelValue="emitPayload"
          />
          <FormTimeInput
            :label="$t('projects.endpoints.hour')"
            v-if="
              payload.schedule_type === 'value' &&
              (payload.frequency === 'day' || payload.frequency === 'weekday' || payload.frequency === 'month' || payload.frequency === 'bi-month' || payload.frequency === 'quarter')
            "
            v-model="time"
            class="mt-2 ml-2 w-25"
            @update:modelValue="emitPayload"
          />
        </div>
        <div class="italic text-12 text-gray-700 mt-2">{{ getScheduledTaskText(payload) }}</div>
        <VariableSelect
          v-model="payload.data_binding"
          multiple
          :label="$t('evaluate.data_binding')"
          class="mt-3 flex-grow"
          selectBgColor="bg-white"
          :filter-data-type="['Text', 'Number', 'Email', 'YesNo', 'Chart', 'DataTable']"
          :showCreate="false"
          @update:modelValue="emitPayload"
        />
      </template>
      <div v-if="isAgent" class="text-11 text-gray-400 mt-2">The agent sets the schedule and inputs at runtime. You only choose the flow to run.</div>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import debounce from '../../../helpers/debounce';
import uuidv4 from '../../../helpers/uuid';
import { MINUTES, HOURS, DAYS, WEEKDAYS } from '../../../constants';
import EvaluateItemsConfig from './EvaluateItemsConfig';

export default {
  name: 'ActionScheduleConfig',
  props: {
    item: {
      type: Object,
      default: null,
    },
    isAgent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      fetching: false,
      dayList: [...Array(30).keys()].map((i) => ({ label: i + 1, value: i + 1 })),
      minuteList: MINUTES,
      hoursList: HOURS,
      daysList: DAYS,
      weekDayList: WEEKDAYS,
      payload: structuredClone(EvaluateItemsConfig['action-schedule'].payload),
    };
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectFlows', 'getProjectEmailProviders']),
    scheduleOptions() {
      if (this.payload.recurring) {
        return [
          { label: 'Minute', value: 'minute' },
          { label: 'Hour', value: 'hour' },
          { label: 'Day', value: 'day' },
          { label: 'Day of the week', value: 'weekday' },
          { label: 'Month', value: 'month' },
          { label: 'Bi-Monthly', value: 'bi-month' },
          { label: 'Quarterly', value: 'quarter' },
        ];
      }
      return [
        { label: 'In Minute(s)', value: 'minute' },
        { label: 'In Hour(s)', value: 'hour' },
        { label: 'In Day(s)', value: 'day' },
        { label: 'Next Week Day', value: 'weekday' },
      ];
    },
    variables() {
      let variables = [];
      if (this.projectId && Object.keys(this.projectData).length) {
        variables = Object.keys(this.projectData?.[this.projectId]?.variables || {}).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) || [];
      }
      variables = variables.map((variable) => ({
        id: uuidv4(),
        value: variable,
      }));
      return variables;
    },
    projectId() {
      return this.$route.params.projectId;
    },
    flows() {
      return Object.keys(this.getProjectFlows()).map((key) => ({
        label: this.getProjectFlows()[key].name,
        value: key,
      }));
    },
    time: {
      get() {
        return `${this.payload.hour}:${this.payload.minute}`;
      },
      set(value) {
        this.payload.hour = value.split(':')[0];
        this.payload.minute = value.split(':')[1];
      },
    },
  },
  methods: {
    onValueTypeChanged() {
      this.clearRHS();
      this.emitPayload();
    },
    // eslint-disable-next-line
    emitPayload: debounce(async function (val) {
      if (this.payload.schedule_type === 'value') {
        if (this.payload.frequency === 'minute' && (this.payload.schedule > 59 || this.payload.schedule < 0)) {
          this.payload.schedule = 1;
        }
        if (this.payload.frequency === 'hour' && (this.payload.schedule > 23 || this.payload.schedule < 0)) {
          this.payload.schedule = 1;
        }
        if (this.payload.frequency === 'day' && (this.payload.schedule > 30 || this.payload.schedule < 1)) {
          this.payload.schedule = 1;
        }
        if (this.payload.frequency === 'weekday' && (this.payload.schedule > 6 || this.payload.schedule < 0)) {
          this.payload.schedule = 1;
        }
      }

      this.$emit('update', {
        id: this.item.id,
        type: this.item.type,
        payload: this.payload,
      });
    }, 300),
    getScheduledTaskText(item) {
      let timeText;
      if (typeof item.hour === 'number' && item.hour < 10) {
        timeText = `0${item.hour}:`;
      } else if (typeof item.hour === 'string' && item.hour.length === 1) {
        timeText = `0${item.hour}:`;
      } else {
        timeText = `${item.hour}:`;
      }
      if (typeof item.minute === 'number' && item.minute < 10) {
        timeText += `0${item.minute} UTC`;
      } else if (typeof item.minute === 'string' && item.minute.length === 1) {
        timeText += `0${item.minute} UTC`;
      } else {
        timeText += `${item.minute} UTC`;
      }
      if (item.recurring) {
        if (item.frequency === 'minute') {
          if (item.schedule === 1) {
            return `Every minute`;
          }
          return `Every ${item.schedule} minutes`;
        }
        if (item.frequency === 'hour') {
          if (item.schedule === 1) {
            return `Every hour`;
          }
          return `Every ${item.schedule} hours`;
        }
        if (item.frequency === 'day') {
          if (item.schedule === 1) {
            return `Everyday at ${timeText}`;
          }
          return `Every ${item.schedule} days at ${timeText}`;
        }
        if (item.frequency === 'month') {
          // Convert all numbers to their ordinal form
          let ordinalDay;
          const lastDigit = item.schedule % 10;
          switch (lastDigit) {
            case 1:
              ordinalDay = `${item.schedule}st`;
              break;
            case 2:
              ordinalDay = `${item.schedule}nd`;
              break;
            case 3:
              ordinalDay = `${item.schedule}rd`;
              break;
            default:
              ordinalDay = `${item.schedule}th`;
              break;
          }
          return `${ordinalDay} day of every month at ${timeText}`;
        }
        if (item.frequency === 'bi-month') {
          // Convert all numbers to their ordinal form
          let ordinalDay;
          const lastDigit = item.schedule % 10;
          switch (lastDigit) {
            case 1:
              ordinalDay = `${item.schedule}st`;
              break;
            case 2:
              ordinalDay = `${item.schedule}nd`;
              break;
            case 3:
              ordinalDay = `${item.schedule}rd`;
              break;
            default:
              ordinalDay = `${item.schedule}th`;
              break;
          }
          return `${ordinalDay} day of every other month at ${timeText}`;
        }
        if (item.frequency === 'quarter') {
          // Convert all numbers to their ordinal form
          let ordinalDay;
          const lastDigit = item.schedule % 10;
          switch (lastDigit) {
            case 1:
              ordinalDay = `${item.schedule}st`;
              break;
            case 2:
              ordinalDay = `${item.schedule}nd`;
              break;
            case 3:
              ordinalDay = `${item.schedule}rd`;
              break;
            default:
              ordinalDay = `${item.schedule}th`;
              break;
          }
          return `${ordinalDay} day of every quarter at ${timeText}`;
        }
        if (item.frequency === 'weekday') {
          const weekday = WEEKDAYS.find((w) => w.value === item.schedule);
          if (weekday) {
            const dayText = WEEKDAYS.find((w) => w.value === item.schedule).label;
            return `Every ${dayText} at ${timeText}`;
          }
        }
      } else {
        if (item.frequency === 'minute') {
          if (item.schedule === 1) {
            return `In ${item.schedule} minute`;
          }
          return `In ${item.schedule} minutes`;
        }
        if (item.frequency === 'hour') {
          if (item.schedule === 1) {
            return `In ${item.schedule} hour`;
          }
          return `In ${item.schedule} hours`;
        }
        if (item.frequency === 'day') {
          if (item.schedule === 1) {
            return `In ${item.schedule} day at ${timeText}`;
          }
          return `In ${item.schedule} days at ${timeText}`;
        }
        if (item.frequency === 'weekday') {
          const weekday = WEEKDAYS.find((w) => w.value === item.schedule);
          if (weekday) {
            const dayText = WEEKDAYS.find((w) => w.value === item.schedule).label;
            return `On ${dayText} at ${timeText}`;
          }
        }
      }
      return '';
    },
  },
  watch: {
    item: {
      handler() {
        if (this.item) {
          this.payload = JSON.parse(JSON.stringify(this.item.payload));
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
